"use client";

import { X } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import type YouTube from "react-youtube";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { useHashParams } from "~/hooks/useHashParams";
import { cn } from "~/lib/utils";
import { type SelectTranslation } from "~/server/db/translations/schema";
import { type SelectVideo } from "~/server/db/videos/schema";
import { useVideoControls } from "~/state/videos/hooks/use-video-controls";

import { VideoHeader } from "./VideoHeader";
import YouTubePlayer from "./YouTubePlayer";

export default function VideoWithTranslation({
  video,
  translation,
  children,
}: {
  video: SelectVideo;
  translation?: SelectTranslation;
  children: React.ReactNode;
}) {
  const { sourceId } = video;

  const [seekTime, setSeekTime] = useState<number | null>(null);
  const [start, setStart] = useState<number | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [player, setPlayer] = useState<YouTube | null>(null);
  const { isAutoPauseEnabled } = useVideoControls();
  const [playTiming, setPlayTiming] = useState<number | null>(null);
  const [ref, inView] = useInView({ initialInView: true });

  const searchParams = useSearchParams();
  const { hashParams } = useHashParams();
  const cursor = searchParams.get("cursor");

  useEffect(() => {
    const { start, duration, playTiming } = hashParams;
    if (start && duration) {
      setSeekTime(Number(start));
      setDuration(Number(duration));
      setPlayTiming(Number(playTiming));
      setStart(null);
    }
  }, [hashParams, player]);

  const handleSeekChange = useCallback((start: number) => {
    setStart(start);
    setSeekTime(null);
    setDuration(null);
  }, []);

  useEffect(() => {
    if (cursor) setSeekTime(Number(cursor));
  }, [cursor]);

  return (
    <div className="">
      <div ref={ref} className="rounded-r-md border bg-background/70">
        <div className="flex flex-col md:flex-row">
          <div className="w-full md:w-1/2 md:pr-2">
            <MiniPlayer inView={inView}>
              <YouTubePlayer
                sourceId={sourceId}
                seekTime={seekTime}
                start={start}
                duration={duration}
                playTiming={playTiming}
                autoPause={isAutoPauseEnabled}
                onSeekChange={handleSeekChange}
                onSetup={setPlayer}
              />
            </MiniPlayer>
          </div>
          <VideoHeader video={video}>
            {translation ? (
              translation.summary
            ) : (
              <div className="space-y-2">
                <Skeleton className="w-144 h-6" />
                <Skeleton className="h-6 w-64" />
                <Skeleton className="h-6 w-96" />
              </div>
            )}
          </VideoHeader>
        </div>
      </div>
      <div className="mt-8 flex-1 pt-4">{children}</div>
    </div>
  );
}

function MiniPlayer({
  children,
  inView,
}: {
  children: React.ReactNode;
  inView: boolean;
}) {
  const { isMiniPlayerHidden, toggleIsMiniPlayerHidden } = useVideoControls();

  return (
    <div
      className={cn(
        "w-full",
        !inView && !isMiniPlayerHidden
          ? "fixed bottom-0 left-0 z-[20] mb-20 h-[25vh] max-h-[180px] px-4 sm:bottom-4 sm:left-4 sm:w-[320px] sm:px-0"
          : "aspect-video",
      )}
    >
      {!inView && !isMiniPlayerHidden && (
        <span className="flex justify-end">
          <Button
            onClick={() => void toggleIsMiniPlayerHidden()}
            variant="ghost"
          >
            <span className="sr-only">Toggle mini player</span>
            <X className="mr-2 h-4 w-4" />
            close
          </Button>
        </span>
      )}
      {children}
    </div>
  );
}
